import { defineStore } from 'pinia';

import { stepConfigs } from '@bv/components/workflow/partials/steps';

import { Flow, FlowNode, ConnectorType, StepConfig } from '../types/workflow';

import { useWorkflowStore } from './workflow';

const generateId = () => `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

export const useWorkflowConnector = defineStore('bvWorkflowConnector', {
  state: () => ({
    // This store doesn't need its own state as it works with the flow from workflow-graph
  }),

  getters: {
    /**
     * Get step configuration for a given step type
     * @param state - The store state
     * @returns Function that takes stepType and returns step config
     */
    getStepConfig:
      () =>
      (stepType: string): StepConfig | null => {
        // Get the step config by type from the object
        const config = stepConfigs[stepType];
        return config || null;
      },
  },

  actions: {
    /**
     * Add a connector to a parent node using connector configuration
     * @param flow - The flow object from workflow-graph store
     * @param parentKey - The ID of the parent node
     * @param connectorConfig - The connector configuration object
     * @returns The ID of the created connector node, or null if failed
     */
    addConnector(
      flow: Flow,
      parentKey: string | number,
      connectorConfig: {
        key: string;
        title: string;
        headerTextColor: string;
        headerColor: string;
        category: 'Connector';
      },
      skipHistory = false,
    ): string | null {
      const store = useWorkflowStore();
      const parentNode = flow[parentKey];
      if (!parentNode) {
        return null;
      }

      // Check if parent is a Router node
      if (parentNode.category !== 'Router') {
        return null;
      }

      // Check connector limits if defined in step config
      const stepConfig = this.getStepConfig(parentNode.type);
      if (stepConfig?.maxConnectors && parentNode.children.length >= stepConfig.maxConnectors) {
        return null;
      }

      const connectorId = generateId();

      if (!skipHistory) {
        store.startHistory();
        store.addToHistory(parentKey);
      }

      store.addToHistory(connectorId);

      const connectorNode: FlowNode = {
        id: connectorId,
        parentId: parentKey,
        name: connectorConfig.title,
        category: 'Connector',
        type: connectorConfig.key,
        children: [],
        data: {},
        connectorType: connectorConfig.key as ConnectorType,
      };

      // Add to flow first
      flow[connectorId] = connectorNode;

      // Find the elsePath connector to insert before it
      const elsePathIndex = parentNode.children.findIndex((childId) => {
        const childNode = flow[childId];
        return childNode && childNode.type === 'ElsePath';
      });

      if (elsePathIndex !== -1) {
        // Insert before the elsePath connector
        parentNode.children.splice(elsePathIndex, 0, connectorId);
      } else {
        // If no elsePath found, add at the end
        parentNode.children.push(connectorId);
      }

      // Ensure elsePath is always last
      this.ensureElsePathLast(flow, parentKey);

      // we don't want to endHistory here, but in the caller of this addConnector (addBranch).
      if (!skipHistory) {
        store.endHistory();
      }
      return connectorId;
    },

    /**
     * Ensure the elsePath connector is always the last child
     * @param flow - The flow object from workflow-graph store
     * @param parentKey - The ID of the parent node
     */
    ensureElsePathLast(flow: Flow, parentKey: string | number): void {
      const parentNode = flow[parentKey];
      if (!parentNode) {
        return;
      }

      // Find the elsePath connector
      const elsePathIndex = parentNode.children.findIndex((childId) => {
        const childNode = flow[childId];
        return childNode && childNode.type === 'ElsePath';
      });

      if (elsePathIndex !== -1 && elsePathIndex !== parentNode.children.length - 1) {
        // Move elsePath to the end
        const elsePathId = parentNode.children[elsePathIndex];
        parentNode.children.splice(elsePathIndex, 1);
        parentNode.children.push(elsePathId);
      }
    },

    /**
     * Remove a connector from a parent node
     * @param parentKey - The ID of the parent node
     * @param connectorKey - The key/type of the connector to remove
     */
    removeConnectorNode(routerNodeId: string | number, connectorNodeId: string | number, skipHistory = false) {
      const store = useWorkflowStore();
      const routerNode = store.flow[routerNodeId];
      if (!routerNode || routerNode.category !== 'Router') {
        return;
      }

      if (!skipHistory) {
        store.startHistory();
        store.addToHistory(routerNodeId);
      }

      store.addToHistory(connectorNodeId);

      // Remove from router node's children
      routerNode.children = routerNode.children.filter((id) => id !== connectorNodeId);

      // Delete the connector node and its descendants
      store.deleteNode(connectorNodeId, true, true);

      // we don't want to endHistory here, but in the caller of this removeConnector (removeBranch).
      if (!skipHistory) {
        store.endHistory();
      }
    },

    /**
     * Get all connectors for a parent node
     * @param flow - The flow object from workflow-graph store
     * @param parentKey - The ID of the parent node
     * @returns Array of connector nodes
     */
    getConnectors(flow: Flow, parentKey: string | number): FlowNode[] {
      const parentNode = flow[parentKey];
      if (!parentNode) {
        return [];
      }

      return parentNode.children
        .map((childId) => flow[childId])
        .filter((node) => node && node.category === 'Connector');
    },

    /**
     * Check if a parent node can have more connectors added
     * @param flow - The flow object from workflow-graph store
     * @param parentKey - The ID of the parent node
     * @returns True if more connectors can be added
     */
    canAddConnector(flow: Flow, parentKey: string | number): boolean {
      const parentNode = flow[parentKey];
      if (!parentNode || parentNode.category !== 'Router') {
        return false;
      }

      const stepConfig = this.getStepConfig(parentNode.type);
      if (stepConfig?.maxConnectors) {
        return parentNode.children.length < stepConfig.maxConnectors;
      }

      return true;
    },

    /**
     * Check if a connector can be removed from a parent node
     * @param flow - The flow object from workflow-graph store
     * @param parentKey - The ID of the parent node
     * @returns True if a connector can be removed
     */
    canRemoveConnector(flow: Flow, parentKey: string | number): boolean {
      const parentNode = flow[parentKey];
      if (!parentNode || parentNode.category !== 'Router') {
        return false;
      }

      const stepConfig = this.getStepConfig(parentNode.type);
      if (stepConfig?.minConnectors) {
        return parentNode.children.length > stepConfig.minConnectors;
      }

      return true;
    },

    /**
     * Generate a dynamic connector title based on context
     * @param flow - The flow object from workflow-graph store
     * @param parentKey - The ID of the parent node
     * @param baseTitle - The base title for the connector
     * @param connectorType - The type of connector
     * @param numberingKey - Optional key to use for counting (defaults to connectorType)
     * @returns A dynamic title for the connector
     */
    generateConnectorTitle(
      flow: Flow,
      parentKey: string | number,
      baseTitle: string,
      _: string = 'Branch',
      __?: string,
    ): string {
      const parentNode = flow[parentKey];
      if (!parentNode) {
        return baseTitle;
      }

      // Get existing connectors based on the counting key
      const existingConnectors = parentNode.children
        .map((childId) => flow[childId])
        .filter((node) => {
          if (!node) {
            return false;
          }
          // Check if the node's name starts with the baseTitle
          return node.name && node.name.startsWith(baseTitle);
        });

      // If no existing connectors, return the base title
      if (existingConnectors.length === 0) {
        return baseTitle;
      }

      // Generate a numbered title starting from 02
      const connectorNumber = existingConnectors.length + 1;
      const paddedNumber = connectorNumber.toString().padStart(2, '0');
      return `${baseTitle} ${paddedNumber}`;
    },
  },
});
